/**
 * Generic portal-api client (Stage 9).
 *
 * A small shared wrapper that attaches the subscriber's Cognito id-token as a
 * `Authorization: Bearer` header (matching the client-auth Lambda authoriser),
 * the same pattern pow-api.ts already uses. Broadcast/CJI/photo features all
 * route through this so auth + error handling live in one place.
 */
import { fetchAuthSession } from 'aws-amplify/auth';
import { tokenMatchesTab } from './tab-identity';
// ⭐ ADR-120 Amendment 4 — the portal API ORIGIN is per-zone and is resolved at sign-in,
// so it is read from the session, not from the build-time `ENV`. A customer of a zone
// other than the one this bundle was built for would otherwise send every `/portal/*`
// call to the WRONG zone's API carrying a token that zone cannot validate — a 401 that
// presents as an expired session and is not one.
import { activePortalApiBaseUrl } from './zone-session';
import { getMniSession, exitMniSession, SESSION_FATAL_CODES } from './impersonation-session';

export interface PortalErrorBody {
  error: { code: string; message: string; [extra: string]: unknown };
}

export class PortalApiError extends Error {
  constructor(
    public status: number,
    public body: PortalErrorBody | undefined,
    message?: string,
  ) {
    super(message ?? body?.error?.message ?? `HTTP ${status}`);
    this.name = 'PortalApiError';
  }
  get code(): string {
    return this.body?.error?.code ?? 'UNKNOWN';
  }
}

async function authHeader(): Promise<Record<string, string>> {
  try {
    const session = await fetchAuthSession();
    // Q13 — never send a call as somebody other than the person this tab is showing. A refused
    // call leaves the tab (reload into the new person, or sign-in); it goes out unauthenticated
    // in the moment before the page unloads, and is answered 401 — never with another's data.
    if (!tokenMatchesTab(session.tokens?.idToken?.payload?.sub)) return {};
    const token = session.tokens?.idToken?.toString();
    return token ? { Authorization: `Bearer ${token}` } : {};
  } catch {
    return {};
  }
}

/**
 * The session check, bounded by the call's own timeout (28 October 2026).
 *
 * ⚠ THE FETCH TIMEOUT NEVER COVERED THIS STEP. `fetchAuthSession()` runs BEFORE `fetch()`, and it
 * can wait on a token refresh or Amplify's own session lock with no timeout of its own. A call
 * stuck here never reached the network — and, coalesced, every identical GET after it (Retry
 * included) joined the same never-settling promise. The Launchpad showed "Loading your Launchpad…"
 * for over 40 s in a UAT run (27 October 2026, AU, `dev` fixture) while no request from that browser
 * reached the API: not the account service, not reporting, not the authorizer. Now the session
 * check gets the same budget as the call, and runs out as a 408 like a timed-out fetch.
 */
async function authHeaderWithin(ms: number, label: string): Promise<Record<string, string>> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  try {
    return await Promise.race([
      authHeader(),
      new Promise<never>((_, reject) => {
        timer = setTimeout(() => reject(new PortalApiError(408, undefined, `${label} timed out (session check)`)), ms);
      }),
    ]);
  } finally {
    if (timer !== undefined) clearTimeout(timer);
  }
}

export interface PortalApiInit extends RequestInit {
  /** Statuses to treat as a valid "no data yet" outcome rather than an error throw. */
  allowedStatuses?: number[];
  /**
   * Journeys-punchlist-5 (ADR-096 Am6) — abort the request after this many ms.
   * Defaults to {@link DEFAULT_TIMEOUT_MS}. A bare `fetch()` with no timeout was
   * the ROOT CAUSE of the silent-fallback bug: when the fetch storm saturated the
   * browser's per-origin connection pool, the live-journeys request queued and
   * hung ~45s until the browser abandoned it — long enough for the panel to have
   * silently painted the frozen snapshot. A deterministic timeout turns an
   * indefinite hang into a fast, named fallback the caller can act on (Retry).
   */
  timeoutMs?: number;
  /**
   * Collapse this GET into any identical in-flight GET (default: true for GET).
   * Journeys-punchlist-5 (ADR-096 Am6) — the CJI dashboard mounts several panels
   * that each independently resolve the same report/dashboard, firing 6× / 5×
   * duplicate GETs per load (the "fetch storm" that starved the renderer and forced
   * mass Lambda cold-starts). Concurrent identical GETs now share ONE network call,
   * so one page load makes one call per resource. Never applied to mutations.
   */
  coalesce?: boolean;
}

/** Safety-net timeout for every portal-api call — no request may hang indefinitely. */
const DEFAULT_TIMEOUT_MS = 30_000;

/**
 * In-flight GET coalescing map (ADR-096 Am6). Keyed by method+path+allowedStatuses+
 * impersonation so only genuinely identical reads share a promise. An entry lives
 * ONLY for the duration of the in-flight request (removed in `finally`), so this is
 * request-coalescing, not a result cache — it never returns data older than a
 * currently-in-flight call, and sequential calls (e.g. a post-mutation reload) are
 * never merged.
 */
const inFlightGets = new Map<string, Promise<{ status: number; data: unknown }>>();

/** Compose the caller's AbortSignal (if any) with a timeout signal. */
function withTimeout(signal: AbortSignal | null | undefined, timeoutMs: number): AbortSignal {
  const timeout = AbortSignal.timeout(timeoutMs);
  if (!signal) return timeout;
  // AbortSignal.any composes multiple signals (either aborts the request).
  const anyFn = (AbortSignal as unknown as { any?: (s: AbortSignal[]) => AbortSignal }).any;
  return anyFn ? anyFn([signal, timeout]) : timeout;
}

/**
 * Authenticated JSON call to portal-api. `allowedStatuses` lets callers treat
 * e.g. 404 as a valid "no data yet" outcome rather than an error throw. Every
 * call carries a timeout (no indefinite hangs) and identical concurrent GETs are
 * coalesced into one network request (see {@link PortalApiInit}).
 */
export async function portalApi<T>(
  path: string,
  init: PortalApiInit = {},
): Promise<{ status: number; data: T | null }> {
  const { allowedStatuses, timeoutMs, coalesce, signal, ...rest } = init;
  const method = (rest.method ?? 'GET').toUpperCase();
  // ADR-081 Stage 4 — client mode: while a consent-gated impersonation session
  // is held, every portal-api call carries it. The server confines the session
  // to the granted client account and applies the D7 ring-fence.
  const mni = getMniSession();

  // ── GET coalescing (ADR-096 Am6) — collapse the fetch storm. Only pure GETs
  //    with no custom body are eligible; mutations never share. ──
  const canCoalesce = (coalesce ?? true) && method === 'GET' && rest.body == null;
  if (canCoalesce) {
    const key = `${method}::${path}::${(allowedStatuses ?? []).join(',')}::${mni?.token ?? ''}`;
    const existing = inFlightGets.get(key);
    if (existing) return existing as Promise<{ status: number; data: T | null }>;
    const promise = doFetch<T>(path, { allowedStatuses, timeoutMs, signal, ...rest }, method, mni)
      .finally(() => inFlightGets.delete(key));
    inFlightGets.set(key, promise as Promise<{ status: number; data: unknown }>);
    return promise;
  }
  return doFetch<T>(path, { allowedStatuses, timeoutMs, signal, ...rest }, method, mni);
}

async function doFetch<T>(
  path: string,
  init: PortalApiInit,
  method: string,
  mni: ReturnType<typeof getMniSession>,
): Promise<{ status: number; data: T | null }> {
  const { allowedStatuses, timeoutMs, signal, ...rest } = init;
  // ONE budget for the whole call: the session check spends from it, the fetch gets the rest.
  const budget = timeoutMs ?? DEFAULT_TIMEOUT_MS;
  const started = Date.now();
  const headers = {
    'Content-Type': 'application/json',
    ...(await authHeaderWithin(budget, `${method} ${path}`)),
    ...(mni ? { 'x-nexa-impersonation': mni.token } : {}),
    ...(rest.headers ?? {}),
  };
  let res: Response;
  try {
    res = await fetch(`${activePortalApiBaseUrl()}${path}`, {
      ...rest,
      headers,
      signal: withTimeout(signal, Math.max(1, budget - (Date.now() - started))),
    });
  } catch (err) {
    // A timeout surfaces as an AbortError/TimeoutError — normalise to a
    // PortalApiError(408) so callers distinguish "request never completed" from a
    // real HTTP status, and fall back deterministically instead of hanging.
    if (err instanceof DOMException && (err.name === 'TimeoutError' || err.name === 'AbortError')) {
      throw new PortalApiError(408, undefined, `${method} ${path} timed out`);
    }
    throw err;
  }
  if (!res.ok && !(allowedStatuses ?? []).includes(res.status)) {
    let body: unknown;
    try {
      body = await res.json();
    } catch {
      /* non-JSON */
    }
    const errBody = body as PortalErrorBody | undefined;
    // A dead session (revoked by the client, expired, invalid) ends client mode:
    // clean up and return the operator to their portal home.
    if (mni && SESSION_FATAL_CODES.has(errBody?.error?.code ?? '')) {
      exitMniSession(mni);
      // Navigation is in flight; surface a terminal error to halt the caller.
      throw new PortalApiError(res.status, errBody, 'Impersonation session ended.');
    }
    throw new PortalApiError(res.status, errBody);
  }
  if (res.status === 204) return { status: res.status, data: null };
  let data: T | null = null;
  try {
    data = (await res.json()) as T;
  } catch {
    data = null;
  }
  return { status: res.status, data };
}

/**
 * Download an authenticated file response (ADR-116 D-C7 — the CSV time exports).
 *
 * ⛔⛔ A PLAIN `window.location.assign('/…/time-export')` DOES NOT WORK, AND FAILS
 * IN THE WORST WAY: a browser navigation carries no `Authorization` header, so
 * the request arrives unauthenticated and the API returns 401 — but the browser
 * has already left the page, so the user sees a blank tab or a downloaded error
 * body rather than an error they can act on. Every authenticated download must
 * go through fetch with the same headers `portalApi` sends.
 *
 * ⚠ NOT `portalApi` ITSELF, because that parses JSON and these responses are
 * `text/csv`. Kept as a sibling so the auth/impersonation header logic is
 * shared and cannot drift, while the body handling differs honestly.
 *
 * Throws `PortalApiError` on a non-2xx so callers surface a toast rather than
 * silently downloading an error page.
 */
export async function portalApiDownload(path: string, fallbackFilename: string): Promise<void> {
  const mni = getMniSession();
  const res = await fetch(`${activePortalApiBaseUrl()}${path}`, {
    headers: {
      ...(await authHeader()),
      ...(mni ? { 'x-nexa-impersonation': mni.token } : {}),
    },
  });
  if (!res.ok) {
    let body: unknown;
    try { body = await res.json(); } catch { /* non-JSON */ }
    throw new PortalApiError(res.status, body as PortalErrorBody | undefined);
  }

  // Prefer the server's own filename when it sent one.
  const disposition = res.headers.get('Content-Disposition') ?? '';
  const match = /filename="?([^";]+)"?/i.exec(disposition);
  const filename = match?.[1] ?? fallbackFilename;

  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  try {
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
  } finally {
    // Revoking synchronously can cancel the download in some browsers; a tick
    // is enough for the click to have been dispatched.
    setTimeout(() => URL.revokeObjectURL(url), 0);
  }
}
